import { useId, useState } from 'react';
import { api, hhmmss, type ApiServerIdentity } from '../api';
import { date as fmtDate } from '@orgazone/date-format';
import { useApp } from '../state';
import { Btn } from './ui';

// L'identité du serveur d'un compte SFTP ou FTPS : l'empreinte confirmée, celle qui attend de l'être, et les trois
// gestes (déclarer celle du fournisseur, confirmer celle présentée, oublier). Employé par le détail d'un transport et
// par le résultat d'un test de connexion.
// ⚠️ DÉCLARER VAUT MIEUX QUE CONFIRMER quand le fournisseur communique son empreinte : la machine compare au caractère
//    près, là où un humain compare mal 43 caractères à l'œil. Confirmer reste pour le fournisseur qui ne la donne pas —
//    c'est alors faire confiance à la connexion, et l'écran le dit.
// ⚠️ « Confirmer » envoie l'empreinte AFFICHÉE : si le serveur en a présenté une autre entre-temps, le serveur refuse
//    plutôt que de confirmer ce que l'opérateur n'a pas lu.
export function ServerIdentityBlock({ transportId, identity, onChanged }: {
  transportId: string; identity: ApiServerIdentity; onChanged: () => Promise<void> | void;
}) {
  const { showToast, guard } = useApp();
  const [busy, setBusy] = useState(false);
  const fpId = useId();
  const [declaring, setDeclaring] = useState(false);
  const [declared, setDeclared] = useState('');
  const act = async (fn: () => Promise<unknown>, success: string) => {
    if (!guard()) return;
    setBusy(true);
    try { await fn(); showToast(success, 'ok'); await onChanged(); }
    catch (e) { showToast('Refusé : ' + (e as Error).message, 'err'); }
    finally { setBusy(false); }
  };
  const kind = identity.kind === 'ssh' ? 'clé d’hôte SSH' : 'certificat TLS';
  const offered = identity.offered;
  /** Déclare l'empreinte saisie ; le message dit si elle correspond à celle que le serveur présente. */
  const declare = async () => {
    if (!guard()) return;
    setBusy(true);
    try {
      const r = await api.declareServerFingerprint(transportId, declared);
      if (r.matchesOffered === false) showToast('Empreinte déclarée — elle DIFFÈRE de celle que le serveur présente : la connexion restera refusée tant qu’elles ne correspondent pas', 'warn');
      else showToast(r.matchesOffered ? 'Empreinte déclarée — c’est bien celle que le serveur présente' : 'Empreinte déclarée — elle sera comparée à la prochaine connexion', 'ok');
      setDeclaring(false);
      setDeclared('');
      await onChanged();
    } catch (e) { showToast('Refusé : ' + (e as Error).message, 'err'); }
    finally { setBusy(false); }
  };
  return (
    <section>
      <div style={{ fontSize: 'var(--t-meta)', fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--ink-500)', marginBottom: 8 }}>Identité du serveur</div>
      <div style={{ display: 'grid', gap: 8 }}>
        {identity.trusted && (
          <div>
            <div style={{ fontSize: 'var(--t-micro)', fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--ink-400)' }}>Empreinte confirmée ({kind})</div>
            <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-800)', marginTop: 2, wordBreak: 'break-all', fontFamily: 'var(--font-mono)' }}>{identity.trusted}</div>
          </div>
        )}
        {!identity.trusted && !offered && (
          <div style={{ fontSize: 'var(--t-dense)', color: identity.kind === 'ssh' ? 'var(--amber-fg)' : 'var(--ink-500)' }}>
            {identity.kind === 'ssh'
              ? 'Aucune empreinte confirmée : la prochaine connexion sera refusée et proposera celle du serveur, à confirmer ici.'
              : 'Aucune empreinte confirmée : un certificat garanti par une autorité reconnue suffit. S’il ne l’est pas (auto-signé), la connexion sera refusée et proposera son empreinte ici.'}
          </div>
        )}
        {offered && (
          <div style={{ display: 'grid', gap: 7, padding: '9px 12px', borderRadius: 'var(--r-xs)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: 'var(--t-dense)' }}>
            <div>
              Le serveur a présenté {identity.trusted ? 'une AUTRE empreinte que celle confirmée' : 'cette empreinte'}
              {identity.offeredAt ? ` le ${fmtDate(identity.offeredAt)} à ${hhmmss(identity.offeredAt)}` : ''}. Vérifiez-la auprès du fournisseur avant de la confirmer :
            </div>
            <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-800)', wordBreak: 'break-all' }}>{offered}</div>
            <div>
              <Btn size="sm" icon="check" disabled={busy} onClick={() => void act(() => api.confirmServerFingerprint(transportId, offered), 'Empreinte confirmée — le serveur est accepté')}>Confirmer cette empreinte</Btn>
            </div>
            <div>Sans la comparer à celle du fournisseur, la confirmer revient à faire confiance à cette connexion. S’il vous l’a donnée, déclarez-la plutôt : la comparaison se fait alors au caractère près.</div>
          </div>
        )}
        {identity.trusted && !offered && (
          <div>
            <Btn size="sm" variant="ghost" disabled={busy} onClick={() => void act(() => api.forgetServerFingerprint(transportId), 'Empreinte oubliée — la prochaine connexion proposera celle du serveur')}>Oublier l’empreinte</Btn>
          </div>
        )}
        {declaring ? (
          <div style={{ display: 'grid', gap: 6 }}>
            <label htmlFor={fpId} style={{ fontSize: 'var(--t-micro)', fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--ink-400)' }}>Empreinte donnée par le fournisseur</label>
            <input id={fpId} className="field-input" value={declared} onChange={(e) => setDeclared(e.target.value)}
              placeholder={identity.kind === 'ssh' ? 'SHA256:…' : 'AB:CD:EF:… (64 chiffres hexadécimaux)'} style={{ fontFamily: 'var(--font-mono)' }} />
            <div style={{ fontSize: 'var(--t-sub)', color: 'var(--ink-400)' }}>
              {identity.kind === 'ssh'
                ? 'L’empreinte SHA-256 de la clé d’hôte, telle que l’affiche ssh-keygen -lf. Une empreinte MD5 est refusée.'
                : 'L’empreinte SHA-256 du certificat, telle que l’affiche openssl x509 -fingerprint -sha256. Elle ne sert qu’à accepter un certificat qu’aucune autorité reconnue ne garantit : un certificat garanti est accepté sans elle.'}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn size="sm" icon="check" disabled={busy || !declared.trim()} onClick={() => void declare()}>Déclarer cette empreinte</Btn>
              <Btn size="sm" variant="ghost" mutating={false} disabled={busy} onClick={() => { setDeclaring(false); setDeclared(''); }}>Annuler</Btn>
            </div>
          </div>
        ) : (
          <div>
            <Btn size="sm" variant="ghost" mutating={false} disabled={busy} onClick={() => setDeclaring(true)}>Déclarer l’empreinte donnée par le fournisseur</Btn>
          </div>
        )}
        <div style={{ fontSize: 'var(--t-sub)', color: 'var(--ink-400)' }}>Aucun identifiant n’est envoyé à un serveur dont l’identité n’est pas vérifiée.</div>
      </div>
    </section>
  );
}
